import { AuthUserDto, LoginRequest, ApiResponse } from '@signalvote/shared';
import { isLegalWall, LegalAcceptanceRequiredError, writesOf } from '@signalvote/api-client';
import { API_BASE_URL, SUBSIDIARY_CODE } from '../config';
import { useCampaignStore } from '../stores';
import { redirectToLegalAccept } from '../lib/legalAccept';
import { onServerWrites } from '../lib/serverWrites';

/** This door bypasses the api-client, so it hands a response's write set on itself. */
function reportWrites(response: Response): Response {
  const writes = writesOf(response);
  if (writes !== null) onServerWrites(writes);
  return response;
}

class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  async login(credentials: LoginRequest): Promise<AuthUserDto> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Subsidiary': SUBSIDIARY_CODE, // Required for multi-tenancy
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Login failed (HTTP ${response.status})`);
      }

      const data: ApiResponse<AuthUserDto> = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error(data.error || 'Login failed - Invalid response');
      }

      const user = data.data;

      // Store token and user data
      localStorage.setItem(this.TOKEN_KEY, user.token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      // Update campaign store with subsidiary info
      const subsidiaryCode = this.extractSubsidiaryCodeFromToken(user.token);
      useCampaignStore.getState().setSubsidiary(
        user.subsidiaryId ?? null,
        subsidiaryCode
      );

      // Immediately refresh user data to ensure ID synchronization
      await this.refreshUserData();

      return user;
    } catch (error) {
      // Enhanced error handling to distinguish between different types of failures
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        // Network error - can't reach the server
        throw new Error(`Cannot connect to server. Please check your internet connection. (API: ${API_BASE_URL})`);
      } else if (error instanceof SyntaxError) {
        // JSON parsing error
        throw new Error('Invalid response from server. Please try again.');
      } else if (error instanceof Error) {
        // Pass through other errors with original message
        throw error;
      } else {
        // Unknown error
        throw new Error('An unexpected error occurred. Please try again.');
      }
    }
  }

  /**
   * Full logout — clears auth tokens AND campaign selection.
   * Use for explicit user-initiated logout only.
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    // Clear campaign store on explicit logout
    useCampaignStore.getState().clear();
  }

  /**
   * Session expiry — clears auth tokens but preserves campaign selection.
   * On re-login the user picks up where they left off.
   */
  clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  getCurrentUser(): AuthUserDto | null {
    try {
      const userJson = localStorage.getItem(this.USER_KEY);
      if (!userJson) return null;
      
      const user = JSON.parse(userJson) as AuthUserDto;
      
      // Check if token is expired (basic check)
      if (!this.isTokenValid(user.token)) {
        this.clearSession();
        return null;
      }

      return user;
    } catch {
      this.clearSession();
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return user !== null;
  }

  private isTokenValid(token: string): boolean {
    try {
      // Basic JWT token validation (check if not expired)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  private extractSubsidiaryCodeFromToken(token: string): string | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.subsidiary_code ?? null;
    } catch {
      return null;
    }
  }

  async refreshToken(): Promise<AuthUserDto | null> {
    const token = this.getToken();
    if (!token) return null;

    const { subsidiaryCode } = useCampaignStore.getState();
    const effectiveSubsidiary = subsidiaryCode || SUBSIDIARY_CODE;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Subsidiary': effectiveSubsidiary,
        },
      });

      if (!response.ok) {
        this.clearSession();
        return null;
      }

      const data: ApiResponse<AuthUserDto> = await response.json();

      if (!data.success || !data.data) {
        this.clearSession();
        return null;
      }

      const user = data.data;

      // Update stored token and user data
      localStorage.setItem(this.TOKEN_KEY, user.token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      return user;
    } catch {
      this.clearSession();
      return null;
    }
  }

  async refreshUserData(): Promise<void> {
    const token = this.getToken();
    if (!token) return;

    const { subsidiaryCode } = useCampaignStore.getState();
    const effectiveSubsidiary = subsidiaryCode || SUBSIDIARY_CODE;

    try {
      // Get current user data from the backend to ensure ID synchronization
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Subsidiary': effectiveSubsidiary,
        },
      });

      if (response.ok) {
        const data: ApiResponse<AuthUserDto> = await response.json();
        
        if (data.success && data.data) {
          const currentUser = this.getCurrentUser();
          if (currentUser) {
            // Update user data while preserving the token
            const updatedUser = { ...data.data, token: currentUser.token };
            localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser));
            console.log('User data refreshed successfully, ID:', updatedUser.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // Don't logout on failure - this is just a sync operation
    }
  }

  // Helper method to make authenticated API calls
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();

    if (!token) {
      throw new Error('No authentication token available');
    }

    // Get multi-tenancy headers from store
    const { subsidiaryCode, selectedCampaign } = useCampaignStore.getState();
    const effectiveSubsidiary = subsidiaryCode || SUBSIDIARY_CODE;

    // Don't set Content-Type for FormData - let browser set it
    const isFormData = options.body instanceof FormData;
    const defaultHeaders: any = {
      'Authorization': `Bearer ${token}`,
      'X-Subsidiary': effectiveSubsidiary,
    };

    if (selectedCampaign?.id) {
      defaultHeaders['X-Campaign-Id'] = selectedCampaign.id;
    }

    if (!isFormData) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const headers = {
      ...defaultHeaders,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If unauthorized, try to refresh token once
    if (response.status === 401) {
      const refreshedUser = await this.refreshToken();
      if (refreshedUser) {
        // Retry with new token
        const newHeaders = {
          ...headers,
          'Authorization': `Bearer ${refreshedUser.token}`,
        };
        
        return this.routeLegalWall(reportWrites(await fetch(url, {
          ...options,
          headers: newHeaders,
        })));
      } else {
        // Refresh failed — clear session but preserve campaign selection
        this.clearSession();
        throw new Error('Authentication failed');
      }
    }

    return this.routeLegalWall(reportWrites(response));
  }

  /**
   * A 409 is either the legal wall or a conflict, and the hooks on this path read a bare 409 as a
   * conflict. Send the wall to the accept screen and throw, as the api-client does, so no hook
   * shows conflict copy while the page unloads. Any other response goes back unread.
   */
  private async routeLegalWall(response: Response): Promise<Response> {
    if (response.status === 409) {
      const body = await response.clone().json().catch(() => null);
      if (isLegalWall(body)) {
        redirectToLegalAccept(body.stale);
        throw new LegalAcceptanceRequiredError(body.stale, body.message);
      }
    }
    return response;
  }

  setAuthFromGetStarted(token: string, user: AuthUserDto): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    const subsidiaryCode = this.extractSubsidiaryCodeFromToken(token);
    useCampaignStore.getState().setSubsidiary(user.subsidiaryId ?? null, subsidiaryCode);
  }

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Subsidiary': SUBSIDIARY_CODE,
        },
        body: JSON.stringify({ email }),
      });

      const data: ApiResponse<string> = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return {
        success: data.success,
        message: data.message || 'Reset email sent successfully'
      };
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Cannot connect to server. Please check your internet connection.');
      } else if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Failed to send reset email');
      }
    }
  }

  async resetPassword(email: string, token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Subsidiary': SUBSIDIARY_CODE,
        },
        body: JSON.stringify({
          email,
          token,
          newPassword
        }),
      });

      const data: ApiResponse<string> = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      return {
        success: data.success,
        message: data.message || 'Password reset successfully'
      };
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Cannot connect to server. Please check your internet connection.');
      } else if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Failed to reset password');
      }
    }
  }
}

export const authService = new AuthService();